/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ============================================================
   GP COMPACT GALLERY — thumbnail switching + variant sync
   Uses event delegation so it survives product-rerender updates.
   ============================================================ */
(function () {
  // Sync the thumbnail strip height to exactly match the active main image
  function gpSyncGalleryHeight(gallery) {
    var galleries = gallery ? [gallery] : document.querySelectorAll('.gp-compact-gallery');
    galleries.forEach(function (g) {
      var activeSlide = g.querySelector('.gp-compact-gallery__slide.is-active');
      var img = activeSlide && activeSlide.querySelector('.gp-compact-gallery__main-img');
      var thumbs = g.querySelector('.gp-compact-gallery__thumbs');
      if (!img || !thumbs) return;
      var h = img.offsetHeight;
      if (h > 0) thumbs.style.maxHeight = h + 'px';
    });
  }

  function switchSlide(gallery, mediaId) {
    var id = String(mediaId);
    gallery.querySelectorAll('.gp-compact-gallery__slide').forEach(function (s) {
      s.classList.toggle('is-active', s.dataset.mediaId === id);
    });
    gallery.querySelectorAll('.gp-compact-gallery__thumb').forEach(function (t) {
      t.classList.toggle('is-active', t.dataset.mediaId === id);
    });
    gpSyncGalleryHeight(gallery);
  }

  // Thumbnail clicks
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('.gp-compact-gallery__thumb');
    if (!thumb) return;
    var gallery = thumb.closest('.gp-compact-gallery');
    if (!gallery) return;
    switchSlide(gallery, thumb.dataset.mediaId);
  });

  // Keep in sync when the shopper picks a different variant
  document.addEventListener('variant:changed', function (e) {
    var variant = e.detail && e.detail.variant;
    if (!variant || !variant.featured_media) return;
    var gallery = document.querySelector('.gp-compact-gallery');
    if (!gallery) return;
    switchSlide(gallery, variant.featured_media.id);
  });

  // Sync height on load and resize
  document.addEventListener('DOMContentLoaded', function () { gpSyncGalleryHeight(); });
  window.addEventListener('resize', function () { gpSyncGalleryHeight(); });

  // Sync after the main image finishes loading (image may arrive after DOM ready)
  document.addEventListener('load', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('gp-compact-gallery__main-img')) {
      gpSyncGalleryHeight();
    }
  }, true);
})();


/* ============================================================
   GP WIDGET CONTROLS — dismissible chatbot + discount widget
   ============================================================ */
(function () {
  if (window.innerWidth > 999) return; // mobile only

  var GP_CLOSE_BTN_CSS = [
    '.gp-widget-close {',
    '  position:absolute; top:-8px; right:-8px;',
    '  width:20px; height:20px; border-radius:50%;',
    '  background:#fff; border:1px solid #ccc;',
    '  cursor:pointer; font-size:12px; line-height:20px;',
    '  text-align:center; z-index:9999; box-shadow:0 1px 4px rgba(0,0,0,.2);',
    '  display:flex; align-items:center; justify-content:center; padding:0;',
    '}',
    '.gp-widget-close:hover { background:#f5f5f5; }',
  ].join('');

  var styleEl = document.createElement('style');
  styleEl.textContent = GP_CLOSE_BTN_CSS;
  document.head.appendChild(styleEl);

  function addCloseButton(wrapper, onClose) {
    if (wrapper.querySelector('.gp-widget-close')) return;
    wrapper.style.position = 'relative';
    var btn = document.createElement('button');
    btn.className = 'gp-widget-close';
    btn.setAttribute('aria-label', 'Close');
    btn.innerHTML = '&times;';
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      onClose();
    });
    wrapper.appendChild(btn);
  }

  // --- Shopify Inbox / common chat buttons ---
  function gpInitChat() {
    var chatSelectors = [
      '#shopify-chat',
      '#tidio-chat',
      '#gorgias-chat-container',
      '.fb_dialog',
    ];
    chatSelectors.forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el || el.dataset.gpDismissed) return;
      addCloseButton(el, function () {
        el.style.display = 'none';
        el.dataset.gpDismissed = '1';
      });
    });
  }

  // --- Discount / "10% Off" bar ---
  // Targets elements containing "10%" or "off" that are fixed-position near the bottom
  function gpInitDiscountBar() {
    var fixed = document.querySelectorAll('body > *:not(script):not(style):not(noscript)');
    fixed.forEach(function (el) {
      if (el.dataset.gpDismissed) return;
      var style = window.getComputedStyle(el);
      var isFixed = (style.position === 'fixed' || style.position === 'sticky');
      var isBottom = parseInt(style.bottom, 10) < 80;
      var text = (el.textContent || '').toLowerCase();
      var looksLikeDiscount = text.indexOf('10%') !== -1 || text.indexOf('% off') !== -1 || text.indexOf('discount') !== -1 || text.indexOf('coupon') !== -1;
      if (!isFixed || !isBottom || !looksLikeDiscount) return;

      // Move it to the top on mobile so it doesn't fight the sticky bar
      el.style.top = '0';
      el.style.bottom = 'auto';
      el.style.zIndex = '8000';

      addCloseButton(el, function () {
        el.style.display = 'none';
        el.dataset.gpDismissed = '1';
      });
    });
  }

  // Run after page loads and also watch for late-injected widgets
  function gpInitWidgets() {
    gpInitChat();
    gpInitDiscountBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', gpInitWidgets);
  } else {
    gpInitWidgets();
  }
  // Re-run after 2s for widgets injected after DOMContentLoaded
  setTimeout(gpInitWidgets, 2000);
  setTimeout(gpInitWidgets, 5000);
})();

